import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch, API } from './api';
import { OrderNo } from './OrderNo';
import { makeScanGuard } from './scanGuard';
import { useScanWedge } from './scanWedge';
import { track, trackLang } from './pp/track';
import { packingText } from './packingText';


interface Product {
  /** the order LINE's identity — what "not found" is keyed by (server: enrich_order_products) */
  line_id?: string;
  product_id: string;
  /** the line's variant in the shop (server: enrich_order_products) */
  variation_id?: string;
  product_code?: string;
  name: string;
  picklist: string;
  barcode: string;
  location: string;
  quantity: number;
  scanned: number;
  /** of `scanned`, how many were booked by tapping the row instead of scanning a barcode */
  manual?: number;
  /** the strings the scanner actually read for this row, one per scanned unit */
  scanned_codes?: string[];
  size_sort?: number | null;
}

/** What the screen packed, per line — sent with the order_packed event and kept on the
 *  order (orders.packed_items). The ORDER stays what was ordered; this is what SHIPPED
 *  (Fernando, 2026-09-21, after a wrong size left with a green scan). `barcodes` are the
 *  scanned strings themselves; whether they belong to another variant is decided when
 *  read, against that day's product data. */
export interface PackedLine {
  line_id: string; variation_id: string | null; product_code: string | null;
  barcode: string | null; barcodes: string[]; qty: number; manual: number;
}

// Sort items the way a packer works a shelf: location letter (A→Z),
// then location number (ascending), then size (small→big) as the tiebreaker.
// Platform-agnostic — relies only on the generic location / size_sort fields.
function parseLocation(loc: string): { letter: string; num: number } {
  const m = (loc || '').trim().match(/^([A-Za-z]+)\s*0*(\d+)?/);
  return {
    letter: (m?.[1] || '~').toUpperCase(),
    num: m && m[2] ? parseInt(m[2], 10) : Number.MAX_SAFE_INTEGER,
  };
}

function sortByLocation<T extends { location: string; size_sort?: number | null }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const la = parseLocation(a.location), lb = parseLocation(b.location);
    if (la.letter !== lb.letter) return la.letter < lb.letter ? -1 : 1;
    if (la.num !== lb.num) return la.num - lb.num;
    return (a.size_sort ?? Number.MAX_SAFE_INTEGER) - (b.size_sort ?? Number.MAX_SAFE_INTEGER);
  });
}

// ONE row = ONE order line. Sizes of the same garment share product_id (Shopify parent
// id; every WGR line even carries product_id "") — keying a row update by product_id
// marked sibling sizes, or the whole order, packed on one tap (external review,
// 2026-09-16). line_id is the server's per-line identity, the same key the line's Can't find sends.
function rowKey(p: Product): string {
  return p.line_id || `${p.product_id}|${p.barcode}|${p.picklist}`;
}

// Reset scan counts and apply packer sort order in one place.
function prepProducts(products: Product[]): Product[] {
  return sortByLocation(products.map(p => ({ ...p, scanned: 0 })));
}

interface Order {
  id: string;
  /** Country order number (SE61709). Shown to the packer; `id` stays the API key. */
  order_number?: string;
  customer: string;
  carrier: string;
  flag: string;
  products: Product[];
}

interface Props {
  orderIds: string[];
  scanMode?: boolean;
  /** Items verified and the note re-scanned. `packed` = what was booked per line (PackedLine). */
  onPackComplete?: (packed?: PackedLine[]) => void;
  onClose: () => void;
  /** Rendered above the product table. PackingSession puts the pack-time
   *  shipping-label status here ("Label printed ✓", "Print label", "already
   *  printed…"); the grid route passes nothing. */
  labelBanner?: React.ReactNode;
  /** After the Issue button moved the current order to Can't find / Hard to
   *  find. The session goes back to "scan the next note"; the grid route
   *  shows the next order (default when the prop is absent). */
  onIssue?: (info: { tabLabel: string; missing: number }) => void;
  /** Guided rounds (2026-10-17): "Pack" without a scan needs the right — the station asks first (its own right, or
   *  someone who has it scans their badge). false = not packed. */
  manualGate?: (line: { line_id?: string; product_code?: string; name?: string }) => Promise<boolean>;
  /** Guided station (Fernando 21.10): the packer is new — among their first orders with 2+ of one item
   *  (pp_guided.scan_note → beginner_multi_qty) — so such an order opens with "scan each piece", acknowledged first. */
  beginnerMultiQty?: boolean;
  /** pp_tips state for the packer (scan_note): "specials" = "Ä and Ö are right here at the station" (21.10) */
  tips?: { specials?: { show: boolean; can_dismiss: boolean; seen?: number } };
  /** the floor model's special places (Ä, Ö — pick_specials): taken at the station while packing */
  specialPlaces?: { code: string; note?: string | null }[];
  /** the tablet's Can't find for this order (pp_guided.scan_note short_lines, 21.10): told at packing, the packer
   *  decides — found it → scan; still missing → Issue → Can't find */
  shortLines?: { line_id: string; product_code: string; location: string | null; qty: number }[];
}

type Ask = { kind: 'short' | 'multi' | 'specials'; lines: Product[] };
const normLoc = (s: string | null | undefined) => (s || '').toUpperCase().replace(/\s+/g, '');

// ── Can't find on the item's own line (Fernando 21.10) ─────────────────────────
// "When the packer has scanned all the items the correct ones are green and the missing one is not green — at that
// stage it is super clear what we have and what we don't.. so why should there be a popup where they need to search
// again. Just click issue on the item line and mark can't find and if there are more pcs than one on this line then
// ask how many are missing." The line is marked here; the order moves (POST /orders/{id}/cant-find, the same server
// path as before) once every line is either green or marked. Hard to find sits beside Can't find.
function LineIssue({ prod, left, tx, onMark, onCancel }: {
  prod: Product; left: number; tx: ReturnType<typeof packingText>;
  onMark: (n: number, tab: 'cant_find' | 'hard_to_find') => void; onCancel: () => void;
}) {
  const [n, setN] = useState(left);
  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-6" onClick={e => e.stopPropagation()}>
      <div className="bg-[#1a1d27] border border-gray-700 rounded-2xl w-full max-w-xl p-6">
        <div className="text-white font-bold text-xl">{prod.picklist || prod.name}</div>
        <div className="text-gray-500 text-sm mb-5">{prod.location} · {prod.scanned}/{prod.quantity}</div>
        {left > 1 && (
          <div className="mb-6">
            <div className="text-gray-300 text-base mb-2">{tx.howManyMissing}</div>
            <div className="flex items-center gap-3">
              <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => setN(v => Math.max(1, v - 1))}
                className="w-14 h-14 rounded-xl bg-gray-800 text-white text-2xl font-bold">−</button>
              <div className="w-16 text-center text-4xl font-black text-red-300">{n}</div>
              <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => setN(v => Math.min(left, v + 1))}
                className="w-14 h-14 rounded-xl bg-gray-800 text-white text-2xl font-bold">+</button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-3 justify-end">
          <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={onCancel}
            className="px-5 py-3 bg-gray-700 text-white text-base rounded-lg">{tx.cancel}</button>
          <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => onMark(n, 'hard_to_find')}
            className="px-5 py-3 bg-orange-700 text-white text-base font-bold rounded-lg">{tx.hardToFind}</button>
          <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => onMark(n, 'cant_find')}
            className="px-6 py-3 bg-red-700 text-white text-lg font-black rounded-lg">{tx.cantFindConfirm}</button>
        </div>
      </div>
    </div>
  );
}

function playBeep(type: 'success' | 'error') {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  if (type === 'success') {
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  } else {
    osc.frequency.value = 220;
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
  }
}

export default function PackingWindow({ orderIds, scanMode, onPackComplete, onClose, labelBanner, onIssue, manualGate, beginnerMultiQty, tips, specialPlaces, shortLines }: Props) {
  const tx = packingText(trackLang());
  // Can't find on the line (21.10): the missing pieces per line, the tab, the line being asked about
  const [marked, setMarked] = useState<Record<string, number>>({});
  const [issueTab, setIssueTab] = useState<'cant_find' | 'hard_to_find'>('cant_find');
  const [lineAsk, setLineAsk] = useState<Product | null>(null);
  const [issueBusy, setIssueBusy] = useState(false);
  // The new-people screens (Fernando 21.10), one after the other: "More than one piece", then "Ä and Ö are right here
  // at the station". While one is open, scans are ignored until the packer has pressed its button.
  const [asks, setAsks] = useState<Ask[]>([]);
  const stopRef = useRef(false);
  const askedRef = useRef('');
  const specialsOffRef = useRef(false);            // "Don't remind me anymore" pressed in this session
  const special = new Map((specialPlaces || []).map(p => [normLoc(p.code), p]));
  const placeText = (loc: string) => {
    const c = normLoc(loc);
    if (c === 'Ä') return tx.placeA;
    if (c === 'Ö') return tx.placeO;
    const p = special.get(c);
    return `${loc}: ${p?.note || tx.atStation}`;
  };
  const [{ packingDelay, manualMode }] = useState(() => {
    const s = JSON.parse(localStorage.getItem('wms_packing_settings') || '{}');
    return { packingDelay: s.delay ?? 3, manualMode: s.manualMode ?? true };
  });

  const [orders, setOrders] = useState<Order[]>([]);
  const [current, setCurrent] = useState(0);
  const [products, setProducts] = useState<Product[]>([]);
  // the picker's Can't find, as rows of this order: the line's product, the quantity not found. AFTER the products
  // state: built before it, every order carrying a can't-find mark crashed the screen ("Cannot access 'W' before
  // initialization" — FI47492, round 661, 30.10; the const was read in its temporal dead zone). -- Claude 1.11
  const shortRows: Product[] = (shortLines || []).map(s => {
    const p = products.find(x => (x.line_id || '') === s.line_id) || products.find(x => x.product_code === s.product_code);
    return { ...(p || { product_id: s.product_code, name: s.product_code, picklist: s.product_code, barcode: '', scanned: 0 }),
             location: s.location || p?.location || '', quantity: s.qty } as Product;
  });
  const [scanInput, setScanInput] = useState('');
  const [lastScan, setLastScan] = useState<{ ok: boolean; name: string } | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dupRef   = useRef(makeScanGuard());
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load (and reset the counts) only when the ORDERS change, never when a parent re-renders: PackingSession passes
  // a new [id] array on every render, and the guided station re-renders every 6 s — each time the scanned counts
  // went back to 0 (round 308, 19.10: three accepted scans, every one "3 remaining"). -- Claude
  const idsKey = orderIds.join('|');
  useEffect(() => {
    Promise.all(orderIds.map(id => authFetch(`${API}/orders/${id}`).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })))
      .then(data => {
        setOrders(data);
        if (data[0]) setProducts(prepProducts(data[0].products || []));
        setLoaded(true);
      })
      .catch(e => { setLastScan({ ok: false, name: `Could not load the orders (${e.message || e}) — close and try again` }); setLoaded(true); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [current, loaded]);

  // an order opens with the screens that apply to this packer — once per order
  useEffect(() => {
    if (!loaded) return;
    const o = orders[current];
    if (!o || askedRef.current === o.id) return;
    askedRef.current = o.id;
    const q: Ask[] = [];
    if (shortRows.length) q.push({ kind: 'short', lines: shortRows });       // for everyone: the most important news
    const multi = products.filter(p => p.quantity > 1);
    if (beginnerMultiQty && multi.length) q.push({ kind: 'multi', lines: multi });
    const spec = products.filter(p => special.has(normLoc(p.location)));
    if (tips?.specials?.show && !specialsOffRef.current && spec.length) q.push({ kind: 'specials', lines: spec });
    if (q.length) { stopRef.current = true; setAsks(q); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, current, beginnerMultiQty, tips?.specials?.show]);
  const answer = (dismiss = false) => {
    const o = orders[current];
    const a = asks[0];
    if (!a) return;
    if (a.kind === 'multi') track('button', { name: 'multi_qty_ack', lines: a.lines.length }, o?.id);
    else if (a.kind === 'short') track('button', { name: 'short_ack', lines: a.lines.length }, o?.id);
    else {
      track('button', { name: dismiss ? 'tip_dismiss' : 'tip_ack', tip: 'specials', lines: a.lines.length }, o?.id);
      if (dismiss) specialsOffRef.current = true;
    }
    const rest = asks.slice(1);
    setAsks(rest);
    if (!rest.length) {
      stopRef.current = false;
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };


  const allScanned = products.length > 0 && products.every(p => p.scanned >= p.quantity);
  const missingOf = (p: Product) => Math.min(marked[rowKey(p)] || 0, Math.max(0, p.quantity - p.scanned));
  const anyMarked = products.some(p => missingOf(p) > 0);
  const settled = anyMarked && products.every(p => p.scanned + missingOf(p) >= p.quantity);
  useEffect(() => { setMarked({}); setIssueTab('cant_find'); }, [current, idsKey]);
  const afterIssue = (info: { tabLabel: string; missing: number }) => {
    if (onIssue) { onIssue(info); return; }
    // grid route: this order has left the flow — show the next one, or finish
    if (orders.length <= 1) { onClose(); return; }
    const rest = orders.filter((_, i) => i !== current);
    const next = Math.min(current, rest.length - 1);
    setOrders(rest); setCurrent(next); setProducts(prepProducts(rest[next].products));
    setLastScan(null); setCountdown(null);
  };
  // every line green or marked → the order moves (one move, every marked line)
  useEffect(() => {
    if (!settled || issueBusy) return;
    const o = orders[current];
    if (!o) return;
    const items = products.filter(p => missingOf(p) > 0).map(p => ({ line_id: p.line_id || rowKey(p), missing_qty: missingOf(p) }));
    const total = items.reduce((a, b) => a + b.missing_qty, 0);
    setIssueBusy(true);
    track('button', { name: 'issue_' + issueTab, lines: items.length, missing: total, via: 'line' }, o.id);
    authFetch(`${API}/orders/${encodeURIComponent(o.id)}/cant-find`, { method: 'POST', body: JSON.stringify({ items, tab: issueTab }) })
      .then(async r => {
        const d = await r.json().catch(() => ({} as any));
        setIssueBusy(false);
        if (!r.ok) { setMarked({}); setLastScan({ ok: false, name: d.detail || 'Could not mark the order' }); return; }
        afterIssue({ tabLabel: d.tab_label || issueTab, missing: total });
      })
      .catch(() => { setIssueBusy(false); setMarked({}); setLastScan({ ok: false, name: 'Could not reach the WMS' }); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const startCountdown = useCallback(() => {
    setCountdown(packingDelay);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(countdownRef.current!);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [packingDelay]);

  useEffect(() => {
    if (allScanned && current < orderIds.length - 1 && !manualMode) {
      startCountdown();
      const timer = setTimeout(() => {
        goNext();
      }, packingDelay * 1000);
      return () => {
        clearTimeout(timer);
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    }
  }, [allScanned]);

  const goNext = () => {
    if (current < orders.length - 1) {
      const next = current + 1;
      setCurrent(next);
      setProducts(prepProducts(orders[next].products));
      setLastScan(null);
      setCountdown(null);
    }
  };

  const goPrev = () => {
    if (current > 0) {
      const prev = current - 1;
      setCurrent(prev);
      setProducts(prepProducts(orders[prev].products));
      setLastScan(null);
      setCountdown(null);
      if (countdownRef.current) clearInterval(countdownRef.current);
    }
  };

  const executeScan = useCallback(async (barcode: string) => {
    const trimmed = barcode.trim();
    if (stopRef.current) { setScanInput(''); return; }          // a new-people screen is open: read it first
    if (!trimmed || !orders[current]) return;
    // A scanner double-fire counted an item twice (the count is owned here,
    // per scan). Same guard as PackingSession, at the one choke point both
    // input paths share.
    if (dupRef.current(trimmed)) return;
    setScanInput('');

    // scan-mode: after all items done, treat next scan as delivery-note confirmation.
    // The note's barcode carries the ORDER NUMBER (CZ5438) — never the WMS id,
    // which is a DB key the paper doesn't know (migration 018). id kept as a
    // fallback for any order somehow missing its number.
    if (scanMode && products.every(p => p.scanned >= p.quantity)) {
      const o = orders[current];
      // The same readings the server accepts for the FIRST scan (main._scan_candidates): the
      // tablets' Nordic keyboard types '-' as '+' ("SE62034+R1" for the resend SE62034-R1) and a
      // stray '`' precedes the number. The rescan compared the raw text and refused Natalia's
      // resend after she had packed it (2026-10-07) — the first scan opened it, the second could
      // not close it. Exact first, then the cleaned readings; never a different number.
      const readings = (s: string) => {
        const base = s.toUpperCase();
        const out: string[] = [];
        for (const c of [base, base.replace(/^[`´'^~§¨ ]+/, ''), base.replace(/\+/g, '-'),
                         base.replace(/^[`´'^~§¨ ]+/, '').replace(/\+/g, '-')]) {
          if (c && !out.includes(c)) out.push(c);
        }
        return out;
      };
      const scanned = trimmed.toUpperCase();
      const wanted = [(o.order_number || '').toUpperCase(), o.id.toUpperCase()].filter(Boolean);
      if (readings(trimmed).some(c => wanted.includes(c))) {
        track('note_rescan', { ok: true }, o.id);
        if (onPackComplete) onPackComplete(products.map(p => ({
          line_id: p.line_id || rowKey(p), variation_id: p.variation_id || null, product_code: p.product_code || null,
          barcode: p.barcode || null, barcodes: p.scanned_codes || [], qty: p.scanned, manual: p.manual || 0 })));
      } else {
        track('note_rescan', { ok: false, len: scanned.length }, o.id);
        playBeep('error');
        setLastScan({ ok: false, name: 'Wrong order — scan the correct delivery note' });
      }
      return;
    }

    const res = await authFetch(`${API}/scan`, {
      method: 'POST',
      body: JSON.stringify({ order_id: orders[current].id, barcode: trimmed }),
    });
    const data = await res.json();

    if (data.match) {
      // Backend only confirms the barcode belongs to the order (it always
      // reports scanned:1); the running count is owned here so multi-qty
      // items can be scanned once per unit, capped at the required quantity.
      // The event names the LINE the scan was booked on (ordered vs shipped,
      // 2026-09-21): the barcode alone could not say which line it went to.
      const hit = products.find(p => p.barcode === trimmed && p.scanned < p.quantity);
      if (!hit) {
        // the barcode belongs to the order but every row carrying it is complete: an
        // over-scan. Recorded as such — never as a booked unit (review 2026-09-21).
        track('item_scan', { ok: false, reason: 'over', barcode: trimmed }, orders[current].id);
        playBeep('error');
        setLastScan({ ok: false, name: `${data.product} — already complete` });
        inputRef.current?.focus();
        return;
      }
      track('item_scan', { ok: true, barcode: trimmed,
                           remaining: products.reduce((n, p) => n + Math.max(0, p.quantity - p.scanned), 0) - 1,
                           line_id: hit.line_id || null, variation_id: hit.variation_id || null,
                           product_code: hit.product_code || null },
            orders[current].id);
      playBeep('success');
      setLastScan({ ok: true, name: data.product });
      // One scan = one physical unit: the first unfinished row with this barcode,
      // never every row that shares it (two one-unit lines of the same variant
      // would both complete on a single scan otherwise). The scanned string is kept
      // on the row — it is what packed_items records.
      setProducts(prev => {
        const idx = prev.findIndex(p => p.barcode === trimmed && p.scanned < p.quantity);
        if (idx < 0) return prev;
        return prev.map((p, i) => i === idx ? { ...p, scanned: p.scanned + 1, scanned_codes: [...(p.scanned_codes || []), trimmed] } : p);
      });
    } else {
      track('item_scan', { ok: false, barcode: trimmed }, orders[current].id);
      playBeep('error');
      // say WHAT was scanned (Fernando 20.10: wrong sizes from shelves holding several sizes of one product)
      const sc = data.scanned as { name: string; size: string | null } | null;
      const nd = (data.needed || []) as string[];
      setLastScan({ ok: false, name: sc
        ? (nd.length ? `Wrong size: you scanned ${sc.name} ${sc.size || ''} — this order needs ${nd.join(' or ')}`
                     : `${sc.name}${sc.size ? ' ' + sc.size : ''} is not on this order — wrong product`)
        : `This barcode is not in the WMS (${trimmed}) — wrong product` });
    }

    inputRef.current?.focus();
  }, [orders, current, products, scanMode, onPackComplete]);
  // Global barcode listener — works even when focus moves to a button
  // 28.10: scanWedge — with or without an Enter, physical keys (any keyboard layout); a focused field keeps its keys
  useScanWedge(code => executeScan(code), { enabled: loaded, skipFields: true });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setScanInput(value);
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    if (value.trim()) {
      scanTimerRef.current = setTimeout(() => executeScan(value), 150);
    }
  };

  const handleScan = (e: React.FormEvent) => {
    e.preventDefault();
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    executeScan(scanInput);
  };

  const handleManualPack = async (prod: Product) => {
    if (prod.scanned >= prod.quantity) return;
    if (manualGate && !(await manualGate({ line_id: prod.line_id || undefined, product_code: prod.product_code || undefined,
                                           name: prod.picklist || prod.name }))) return;
    playBeep('success');
    setLastScan({ ok: true, name: prod.picklist || prod.name });
    // a tap is a claim without a barcode: recorded as such (ordered vs shipped, 2026-09-21)
    track('item_scan', { ok: true, manual: true, barcode: prod.barcode || null,
                         line_id: prod.line_id || null, variation_id: prod.variation_id || null,
                         product_code: prod.product_code || null }, orders[current]?.id);
    // one tap = one unit on ONE row, even when two lines share a key (review 5.1)
    setProducts(prev => {
      const idx = prev.findIndex(p => rowKey(p) === rowKey(prod) && p.scanned < p.quantity);
      return idx < 0 ? prev : prev.map((p, i) => i === idx ? { ...p, scanned: p.scanned + 1, manual: (p.manual || 0) + 1 } : p);
    });
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  if (!loaded) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="text-white text-xl">Loading orders...</div>
      </div>
    );
  }

  const order = orders[current];
  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-[#0f1117] z-50 flex flex-col" onClick={() => inputRef.current?.focus()}>
      {/* Header */}
      <div className="bg-[#1a1d27] border-b border-gray-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">
            ✕ Close
          </button>
          <div>
            <span className="text-gray-500 text-xs uppercase tracking-wider">Order</span>
            <div className="flex items-baseline gap-3 leading-tight">
              <OrderNo value={order.order_number || order.id} className="text-white font-black text-2xl" />
              <span className="text-gray-400 text-base italic">{order.customer}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div className="text-right">
            <span className="text-gray-500 text-xs uppercase tracking-wider">Delivery</span>
            <div className="text-white font-semibold">{order.carrier}</div>
          </div>
        </div>
      </div>
      {lineAsk && (
        <LineIssue prod={lineAsk} left={Math.max(1, lineAsk.quantity - lineAsk.scanned)} tx={tx}
          onCancel={() => { setLineAsk(null); stopRef.current = asks.length > 0; }}
          onMark={(n, tab) => {
            track('button', { name: 'line_cant_find', tab, missing: n, line_id: lineAsk.line_id || null }, order.id);
            setMarked(m => ({ ...m, [rowKey(lineAsk)]: n }));
            setIssueTab(tab);
            setLineAsk(null);
            stopRef.current = asks.length > 0;
          }} />
      )}

      {/* Progress bar */}
      <div className="h-1 bg-gray-800">
        <div
          className="h-full bg-blue-500 transition-all duration-300"
          style={{ width: `${((current + 1) / orders.length) * 100}%` }}
        />
      </div>

      {asks[0]?.kind === 'short' && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-6">
          <div className="bg-orange-400 text-black rounded-2xl p-8 max-w-2xl w-full text-center shadow-2xl">
            <div className="text-4xl sm:text-5xl font-black mb-5">🔎 {tx.shortTitle}</div>
            <div className="space-y-1 mb-5">
              {asks[0].lines.map((p, i) => (
                <div key={i} className="text-2xl font-bold">{p.quantity} × {p.picklist || p.name}{p.location ? ` (${p.location})` : ''}</div>
              ))}
            </div>
            <div className="text-xl font-semibold mb-7">{tx.shortBody}</div>
            <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => answer()}
              className="px-8 py-4 rounded-xl bg-black text-white text-2xl font-black">{tx.shortOk}</button>
          </div>
        </div>
      )}
      {asks[0]?.kind === 'multi' && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-6">
          <div className="bg-amber-400 text-black rounded-2xl p-8 max-w-2xl w-full text-center shadow-2xl">
            <div className="text-4xl sm:text-5xl font-black mb-5">⚠ {tx.multiTitle}</div>
            <div className="space-y-1 mb-5">
              {asks[0].lines.map((p, i) => (
                <div key={i} className="text-2xl font-bold">{p.quantity} × {p.picklist || p.name}</div>
              ))}
            </div>
            <div className="text-xl font-semibold mb-7">{tx.multiBody}</div>
            {/* never focused: a scanner's Enter must not press it */}
            <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => answer()}
              className="px-8 py-4 rounded-xl bg-black text-white text-2xl font-black">{tx.multiOk}</button>
          </div>
        </div>
      )}
      {asks[0]?.kind === 'specials' && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-6">
          <div className="bg-sky-300 text-black rounded-2xl p-8 max-w-2xl w-full text-center shadow-2xl">
            <div className="text-4xl sm:text-5xl font-black mb-5">📍 {tx.specialsTitle}</div>
            <div className="space-y-1 mb-5">
              {asks[0].lines.map((p, i) => (
                <div key={i} className="text-2xl font-bold">{p.quantity} × {p.picklist || p.name} — {placeText(p.location)}</div>
              ))}
            </div>
            <div className="text-xl font-semibold mb-7">{tx.specialsBody}</div>
            <div className="flex flex-wrap justify-center gap-3">
              <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => answer()}
                className="px-8 py-4 rounded-xl bg-black text-white text-2xl font-black">{tx.specialsOk}</button>
              {/* not on the person's first P&P day (Fernando 21.10) */}
              {tips?.specials?.can_dismiss && (
                <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => answer(true)}
                  className="px-6 py-4 rounded-xl bg-white/70 text-black text-lg font-bold">{tx.dontRemind}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* the picker's Can't find stays in sight while this order is open */}
      {shortRows.length > 0 && (
        <div className="bg-orange-500/20 border-y border-orange-500/40 px-6 py-2 text-center text-orange-200 text-sm font-semibold">
          🔎 {tx.shortBanner} {shortRows.map(p => `${p.quantity} × ${p.picklist || p.name}${p.location ? ` (${p.location})` : ''}`).join(' · ')}
          <span className="font-normal text-orange-300/80"> — {tx.shortBannerHint}</span>
        </div>
      )}

      {/* Scan feedback banner */}
      {lastScan && (
        <div className={`px-6 py-3 text-sm font-semibold text-center transition-all ${
          lastScan.ok ? 'bg-green-900/60 text-green-300' : 'bg-red-900/60 text-red-300'
        }`}>
          {lastScan.ok ? '✓' : '✗'} {lastScan.name}
        </div>
      )}

      {anyMarked && !settled && (
        <div className="bg-red-800/60 px-6 py-3 text-center text-white font-bold text-base">{tx.markRestHint}</div>
      )}
      {/* All done banner — at the station it says, big, what comes next (Fernando 21.10: new people did not know) */}
      {allScanned && scanMode && (
        <div className="bg-blue-600 px-6 py-5 text-center text-white font-black text-2xl sm:text-3xl">{tx.allScannedNote}</div>
      )}
      {allScanned && !scanMode && (
        <div className="bg-green-700 px-6 py-3 text-center text-white font-bold text-sm">
          {current < orders.length - 1
              ? manualMode
                ? 'All items verified! Press Next → to continue.'
                : `All items verified! Moving to next order in ${countdown ?? 0}s...`
              : 'All items verified! Last order complete.'}
        </div>
      )}

      {labelBanner}

      {/* Products table — scrollable, fills remaining space */}
      <div className="flex-1 overflow-y-auto min-h-0 px-6 pt-4 pb-2">
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                <th className="px-6 py-4 text-left">{tx.colLocation}</th>
                <th className="px-6 py-4 text-left">{tx.colProduct}</th>
                <th className="px-6 py-4 text-center">{tx.colQty}</th>
                <th className="px-6 py-4 text-center">{tx.colScanned}</th>
                <th className="px-4 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((prod, i) => {
                const done = prod.scanned >= prod.quantity;
                const miss = missingOf(prod);
                return (
                  <tr
                    key={i}
                    className={`border-b border-gray-800 last:border-0 transition-colors ${done ? 'bg-green-900/30' : miss ? 'bg-red-900/30' : ''}`}
                  >
                    <td className="px-6 py-5 text-gray-400 font-mono text-lg">
                      {prod.location || <span className="font-sans text-amber-400">— {tx.noShelf}</span>}
                      {special.has(normLoc(prod.location)) && <span className="font-sans text-sm text-sky-300"> · {tx.atStation}</span>}
                    </td>
                    <td className="px-6 py-5">
                      <span className={`text-xl font-semibold ${done ? 'text-green-300' : 'text-white'}`}>
                        {prod.picklist || prod.name}
                      </span>
                    </td>
                    <td className={`px-6 py-5 text-center ${prod.quantity > 1 ? 'text-amber-400' : 'text-white'}`}>
                      <span className="text-2xl font-black">{prod.quantity}</span>
                      {prod.quantity > 1 && <span className="block text-xs font-bold uppercase tracking-wide">{tx.pcs}</span>}
                    </td>
                    <td className="px-6 py-5 text-center">
                      {done
                        ? <span className="text-green-400 text-3xl font-bold">✓</span>
                        : <span className="text-gray-500 text-xl">{prod.scanned}/{prod.quantity}</span>
                      }
                    </td>
                    <td className="px-4 py-5 text-center whitespace-nowrap">
                      {!done && miss > 0 && (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-red-300 font-bold">{tx.missingMark(miss)}</span>
                          <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()}
                            onClick={e => { e.stopPropagation(); setMarked(m => { const c = { ...m }; delete c[rowKey(prod)]; return c; }); }}
                            className="px-3 py-1.5 bg-gray-700 text-white text-sm rounded-lg">{tx.undo}</button>
                        </span>
                      )}
                      {!done && !miss && (
                        <span className="inline-flex items-center gap-2">
                          <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()}
                            onClick={e => { e.stopPropagation(); stopRef.current = true; setLineAsk(prod); }}
                            className="px-4 py-2 bg-red-800 hover:bg-red-700 text-white text-sm font-bold rounded-lg">
                            {tx.cantFindBtn}
                          </button>
                          <button
                            onClick={e => { e.stopPropagation(); handleManualPack(prod); }}
                            className="px-4 py-2 bg-gray-700 hover:bg-blue-600 text-white text-sm font-semibold rounded-lg transition-colors"
                          >
                            {tx.packBtn}
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {/* the delivery note as the last row (Fernando 21.10: "something they are used to look at") */}
              {scanMode && (
                <tr className={`transition-colors ${allScanned ? 'bg-blue-700/40 animate-pulse' : ''}`}>
                  <td className="px-6 py-5 text-gray-400 text-2xl">📄</td>
                  <td className="px-6 py-5">
                    <span className={`text-xl font-semibold ${allScanned ? 'text-white' : 'text-gray-400'}`}>{tx.noteRow}</span>
                    <span className="ml-2 text-sm text-gray-500">— {tx.noteRowHint}</span>
                  </td>
                  <td className="px-6 py-5 text-center text-2xl font-black text-white">1</td>
                  <td className="px-6 py-5 text-center">
                    {allScanned
                      ? <span className="text-blue-100 text-lg font-black">{tx.noteScanNow} ←</span>
                      : <span className="text-gray-600 text-xl">—</span>}
                  </td>
                  <td className="px-4 py-5" />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scanner input — fixed above nav */}
      <div className="flex-none px-6 py-3 bg-[#0f1117]">
        <form onSubmit={handleScan}>
          <input
            ref={inputRef}
            value={scanInput}
            onChange={handleInputChange}
            className="w-full bg-gray-800 border border-gray-700 focus:border-blue-500 text-white rounded-xl px-4 py-3 text-center text-base focus:outline-none"
            placeholder="Scan barcode or click here and scan..."
            autoComplete="off"
          />
        </form>
      </div>

      {/* Navigation — always fixed at bottom */}
      <div className="flex-none bg-[#1a1d27] border-t border-gray-800 px-6 py-4 flex items-center justify-between gap-4">
        {scanMode ? (
          <button
            onClick={onClose}
            className="w-full py-4 text-sm font-medium bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl transition-colors"
          >
            ✕ Abandon order
          </button>
        ) : (
          <>
            <button
              onClick={goPrev}
              disabled={current === 0}
              className="w-48 py-6 text-xl font-bold bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
            >
              ← Prev
            </button>

            <div className="text-center">
              <div className="text-3xl font-black text-white">{current + 1}</div>
              <div className="text-gray-500 text-sm">of {orders.length}</div>
            </div>

            <button
              onClick={goNext}
              disabled={current === orders.length - 1}
              className="w-48 py-6 text-xl font-bold bg-blue-600 hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
            >
              Next →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
